import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Gamepad2, MessageCircle, Calendar, UserRound, LogOut, Trophy, Lock, Globe } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import api from "@/lib/api";
import { fileUrl } from "@/lib/upload";
import { useCustomer } from "@/lib/customer";
import Logo from "@/components/Logo";
import { RulesBlock, hasRules } from "@/components/ContestRules";

const fmt = (iso) => new Date(iso).toLocaleDateString("it-IT", { timeZone: "Europe/Rome", day: "2-digit", month: "short", year: "numeric" });
const waLink = (n, text) => `https://wa.me/${(n || "").replace(/\D/g, "")}${text ? `?text=${encodeURIComponent(text)}` : ""}`;

export default function PublicContests() {
  const navigate = useNavigate();
  const { customer, logout } = useCustomer();
  const [list, setList] = useState(null);
  useEffect(() => {
    api.publicCatalog().then(setList).catch(() => setList([]));
  }, []);
  return (
    <div className="min-h-screen bg-background">
      <header className="sticky top-0 z-40 backdrop-blur-xl bg-background/80 border-b border-border">
        <div className="max-w-5xl mx-auto px-6 py-4 flex items-center gap-3">
          <Logo className="h-10" />
          <div><h1 className="font-display font-bold text-xl">Concorsi</h1><p className="text-xs text-muted-foreground">Gioca e vinci subito</p></div>
          <div className="ml-auto flex items-center gap-2">
            {customer ? (
              <>
                <Button data-testid="my-prizes-button" variant="secondary" size="sm" onClick={() => navigate("/i-miei-premi")} className="gap-1.5"><Trophy className="h-4 w-4" /> I miei premi</Button>
                <span className="text-xs text-muted-foreground hidden sm:inline" data-testid="customer-name">{customer.name || customer.email}</span>
                <Button data-testid="customer-logout-button" variant="ghost" size="sm" onClick={logout}><LogOut className="h-4 w-4" /></Button>
              </>
            ) : (
              <Button data-testid="customer-login-button" size="sm" onClick={() => navigate("/accedi")} className="gap-1.5"><UserRound className="h-4 w-4" /> Accedi / Registrati</Button>
            )}
          </div>
        </div>
      </header>
      <main className="max-w-5xl mx-auto px-6 py-8" data-testid="public-contests">
        {!list && <p className="text-muted-foreground">Caricamento…</p>}
        {list?.length === 0 && (
          <Card className="p-10 text-center border-dashed border-2 bg-card/50"><p className="text-muted-foreground">Nessun concorso attivo al momento. Torna presto!</p></Card>
        )}
        {list?.length > 0 && (
          <div className="grid sm:grid-cols-2 gap-5">
            {list.map((c) => <ContestCard key={c.id} c={c} customer={customer} navigate={navigate} />)}
          </div>
        )}
      </main>
    </div>
  );
}

function ContestCard({ c, customer, navigate }) {
  const logo = fileUrl(c.logo_file_id) || c.logo_url;
  const poster = fileUrl(c.poster_file_id);
  const wa = c.channel === "whatsapp";
  const web = !!c.web_enabled;
  const needsReg = c.access_mode === "registered" || web;
  const active = c.status === "Attivo";
  const target = web ? `/online/${c.id}` : `/gioca/${c.id}`;
  const play = () => {
    if (needsReg && !customer) return navigate(`/accedi?next=${encodeURIComponent(target)}`);
    navigate(target);
  };
  return (
    <Card className="overflow-hidden bg-card border-border flex flex-col" data-testid={`public-contest-${c.id}`}>
      {poster && <img src={poster} alt="" className="h-40 w-full object-cover" />}
      <div className="p-5 space-y-3 flex-1 flex flex-col">
        <div className="flex items-start gap-3">
          {logo && <img src={logo} alt="" className="h-12 w-12 object-contain rounded-md bg-white/5" />}
          <div className="flex-1 min-w-0">
            <h3 className="font-display font-bold text-lg leading-tight">{c.name}</h3>
            <div className="flex flex-wrap gap-1.5 mt-1">
              <Badge className={`border ${active ? "bg-emerald-500/15 text-emerald-400 border-emerald-500/30" : "bg-sky-500/15 text-sky-400 border-sky-500/30"}`}>{c.status}</Badge>
              {!c.web_only && <Badge variant="secondary" className="border border-border gap-1">{wa ? <MessageCircle className="h-3 w-3" /> : <Gamepad2 className="h-3 w-3" />}{wa ? "WhatsApp" : "Totem"}</Badge>}
              {web && <Badge className="border bg-sky-500/15 text-sky-500 border-sky-500/30 gap-1" data-testid={`web-badge-${c.id}`}><Globe className="h-3 w-3" /> {c.web_only ? "Solo online" : "Online"}</Badge>}
              {needsReg && <Badge className="border bg-amber-500/15 text-amber-400 border-amber-500/30 gap-1" data-testid={`needs-registration-${c.id}`}><Lock className="h-3 w-3" /> Con registrazione</Badge>}
            </div>
          </div>
        </div>
        {c.description && <p className="text-sm text-muted-foreground line-clamp-3">{c.description}</p>}
        <p className="text-xs text-muted-foreground flex items-center gap-1.5"><Calendar className="h-3.5 w-3.5" /> {fmt(c.start_datetime)} → {fmt(c.end_datetime)}</p>
        {hasRules(c) ? <RulesBlock c={c} compact /> : null}
        <div className="mt-auto pt-2">
          {wa && !web ? (
            <a href={waLink(c.whatsapp_number)} target="_blank" rel="noreferrer" data-testid={`public-whatsapp-${c.id}`}>
              <Button className="w-full gap-2 bg-emerald-600 hover:bg-emerald-700" disabled={!active}><MessageCircle className="h-4 w-4" /> Gioca su WhatsApp {c.whatsapp_number}</Button>
            </a>
          ) : (
            <Button data-testid={`public-play-${c.id}`} onClick={play} disabled={!active} className="w-full gap-2"><Gamepad2 className="h-4 w-4" /> {needsReg && !customer ? "Registrati e gioca" : web ? "Gioca online" : "Gioca"}</Button>
          )}
        </div>
      </div>
    </Card>
  );
}
